import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FileText, Calendar, Clock, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

/**
 * Pagină listing articole blog
 * URL: /blog
 */

function BlogListPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        const response = await fetch('/seo-data/blog-articles.json');
        const data = await response.json();
        // Sortează după dată (cele mai noi primele)
        const sorted = data.articles.sort((a, b) =>
          new Date(b.publishedAt) - new Date(a.publishedAt)
        );
        setArticles(sorted);
        setLoading(false);
      } catch (error) {
        console.error('Error loading articles:', error);
        setLoading(false);
      }
    };

    loadArticles();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-cyan-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-cyan-50">
      <Helmet>
        <title>Blog - Ghiduri Certificat Constatator ONRC | Constatatoare.ro</title>
        <meta name="description" content="Ghiduri complete și articole utile despre certificatul constatator ONRC. Află tot ce trebuie să știi despre documente oficiale pentru firme." />
        <link rel="canonical" href="https://constatatoare.ro/blog/" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://constatatoare.ro/blog/" />
        <meta property="og:title" content="Blog - Ghiduri Certificat Constatator ONRC" />
        <meta property="og:description" content="Ghiduri complete și articole utile despre certificatul constatator ONRC." />
        <meta property="og:image" content="https://constatatoare.ro/og-image-v2.jpg" />
      </Helmet>

      <Header />

      {/* Hero Section */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-12">
          <nav className="text-sm mb-4">
            <ol className="flex items-center space-x-2 text-gray-600">
              <li>
                <Link to="/" className="hover:text-emerald-600 transition">
                  Acasă
                </Link>
              </li>
              <li>/</li>
              <li className="text-gray-900 font-medium">Blog</li>
            </ol>
          </nav>

          <div className="flex items-center space-x-3 mb-4">
            <FileText className="h-10 w-10 text-emerald-600" />
            <h1 className="text-4xl font-bold text-gray-900">
              Blog & Ghiduri
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-2xl">
            Articole utile despre certificatul constatator, ONRC și documente oficiale pentru firme din România.
          </p>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.map((article) => (
            <Link
              key={article.slug}
              to={`/blog/${article.slug}/`}
              className="group"
            >
              <article className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                {/* Header gradient */}
                <div className="bg-gradient-to-br from-emerald-600 to-cyan-600 p-6 text-white">
                  <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm font-medium mb-3">
                    {article.category}
                  </span>
                  <h2 className="text-xl font-bold group-hover:underline">
                    {article.title}
                  </h2>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-grow">
                  <p className="text-gray-600 mb-4 flex-grow">
                    {article.description}
                  </p>

                  <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {new Date(article.publishedAt).toLocaleDateString('ro-RO')}
                      </span>
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {article.readTime} min
                      </span>
                    </div>
                    <ArrowRight className="h-5 w-5 text-emerald-600 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </article>
            </Link>
          ))}
        </div>

        {articles.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">Nu există articole momentan.</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default BlogListPage;
